import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import {
  Brain,
  TrendingUp,
  AlertTriangle,
  Shield,
  Sparkles,
  MessageSquare,
  BarChart3,
  Target,
  Zap,
  Activity
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const AIInsights = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [dashboardInsights, setDashboardInsights] = useState(null);
  const [incidentAnalysis, setIncidentAnalysis] = useState(null);
  const [predictions, setPredictions] = useState(null);
  const [recommendations, setRecommendations] = useState(null);
  const [chatQuestion, setChatQuestion] = useState('');
  const [chatResponse, setChatResponse] = useState('');
  const [chatLoading, setChatLoading] = useState(false);

  const API_URL = (window.__BACKEND_URL__ || process.env.REACT_APP_BACKEND_URL) || '';

  useEffect(() => {
    loadDashboardInsights();
  }, []);

  const getAuthHeader = () => {
    const token = localStorage.getItem('token');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const loadDashboardInsights = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/ai-insights/dashboard-insights`, getAuthHeader());
      setDashboardInsights(response.data.insights);
    } catch (error) {
      console.error('Erreur chargement insights:', error);
    }
  };

  const analyzeIncidents = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/ai-insights/incident-analysis`, getAuthHeader());
      
      if (response.data.success) {
        setIncidentAnalysis(response.data.analysis);
        toast({
          title: "✨ Analyse IA terminée",
          description: "L'IA a analysé vos incidents avec succès",
        });
      }
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible d'analyser les incidents",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const predictIncidents = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/ai-insights/predict-incidents`, getAuthHeader());
      
      if (response.data.success) {
        setPredictions(response.data.predictions);
        toast({
          title: "🔮 Prédictions générées",
          description: "L'IA a prédit les incidents futurs",
        });
      }
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible de générer les prédictions",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const getRecommendations = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/ai-insights/smart-recommendations`, getAuthHeader());
      
      if (response.data.success) {
        setRecommendations(response.data.recommendations);
        toast({
          title: "💡 Recommandations générées",
          description: "L'IA a analysé vos opérations",
        });
      }
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible de générer les recommandations",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const askAssistant = async () => {
    if (!chatQuestion.trim()) {
      toast({
        title: "Erreur",
        description: "Veuillez poser une question",
        variant: "destructive",
      });
      return;
    }

    setChatLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/ai-insights/assistant-chat`,
        { question: chatQuestion },
        getAuthHeader()
      );
      
      setChatResponse(response.data.response);
      toast({
        title: "🤖 Réponse de l'IA",
        description: "L'assistant a répondu à votre question",
      });
    } catch (error) {
      toast({
        title: "Erreur",
        description: "L'assistant ne peut pas répondre",
        variant: "destructive",
      });
    } finally {
      setChatLoading(false);
    }
  };

  const getRiskColor = (score) => {
    if (score < 30) return 'text-green-600 bg-green-50';
    if (score < 60) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="flex items-center justify-center mb-4">
          <Brain className="w-16 h-16 text-purple-600 mr-4 animate-pulse" />
          <div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-purple-600 via-pink-600 to-blue-600 bg-clip-text text-transparent">
              IA INSIGHTS
            </h1>
            <p className="text-lg text-gray-600 mt-2">
              IA open source (Mistral • Llama • DeepSeek) - Analyses Prédictives
            </p>
          </div>
        </div>
      </div>

      {/* Dashboard Overview */}
      {dashboardInsights && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="border-2 border-purple-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Score de Risque IA</p>
                  <p className={`text-3xl font-bold ${getRiskColor(dashboardInsights.risk_score)}`}>
                    {dashboardInsights.risk_score}/100
                  </p>
                </div>
                <Activity className="w-12 h-12 text-purple-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Sites</p>
                  <p className="text-3xl font-bold text-blue-600">{dashboardInsights.total_sites}</p>
                </div>
                <Shield className="w-12 h-12 text-blue-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Agents</p>
                  <p className="text-3xl font-bold text-green-600">{dashboardInsights.total_agents}</p>
                </div>
                <Target className="w-12 h-12 text-green-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Incidents Récents</p>
                  <p className="text-3xl font-bold text-orange-600">{dashboardInsights.recent_incidents}</p>
                </div>
                <AlertTriangle className="w-12 h-12 text-orange-600" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Actions IA */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Button 
          onClick={analyzeIncidents} 
          disabled={loading}
          className="h-24 text-lg bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
        >
          <BarChart3 className="w-6 h-6 mr-2" />
          {loading ? 'Analyse en cours...' : 'Analyser Incidents IA'}
        </Button>

        <Button 
          onClick={predictIncidents} 
          disabled={loading}
          className="h-24 text-lg bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700"
        >
          <Sparkles className="w-6 h-6 mr-2" />
          {loading ? 'Prédiction...' : 'Prédire Incidents'}
        </Button>

        <Button 
          onClick={getRecommendations} 
          disabled={loading}
          className="h-24 text-lg bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
        >
          <TrendingUp className="w-6 h-6 mr-2" />
          {loading ? 'Génération...' : 'Recommandations IA'}
        </Button>
      </div>

      {/* Analyse des Incidents */}
      {incidentAnalysis && (
        <Card className="border-2 border-purple-300">
          <CardHeader className="bg-gradient-to-r from-purple-50 to-pink-50">
            <CardTitle className="flex items-center">
              <Brain className="w-6 h-6 mr-2 text-purple-600" />
              Analyse IA des Incidents
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="space-y-4">
              <div className="p-4 bg-red-50 rounded-lg">
                <h3 className="font-bold text-red-900 mb-2">Score de Risque: {incidentAnalysis.risk_score}/100</h3>
                <p className="text-sm text-red-700">
                  {incidentAnalysis.trend_analysis === 'stable' && 'Situation stable'}
                  {incidentAnalysis.trend_analysis === 'hausse' && '⚠️ Tendance à la hausse'}
                  {incidentAnalysis.trend_analysis === 'baisse' && '✅ Tendance à la baisse'}
                </p>
              </div>

              {incidentAnalysis.patterns && incidentAnalysis.patterns.length > 0 && (
                <div>
                  <h3 className="font-bold mb-2">🔍 Patterns Détectés</h3>
                  <ul className="space-y-2">
                    {incidentAnalysis.patterns.map((pattern, i) => (
                      <li key={i} className="flex items-start space-x-2">
                        <Zap className="w-4 h-4 text-yellow-600 mt-1 flex-shrink-0" />
                        <span className="text-sm">{pattern}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {incidentAnalysis.recommendations && incidentAnalysis.recommendations.length > 0 && (
                <div>
                  <h3 className="font-bold mb-2">💡 Recommandations</h3>
                  <ul className="space-y-2">
                    {incidentAnalysis.recommendations.map((rec, i) => (
                      <li key={i} className="flex items-start space-x-2">
                        <Target className="w-4 h-4 text-green-600 mt-1 flex-shrink-0" />
                        <span className="text-sm">{rec}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {incidentAnalysis.priority_actions && incidentAnalysis.priority_actions.length > 0 && (
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <h3 className="font-bold text-yellow-900 mb-2">⚡ Actions Prioritaires</h3>
                  <ul className="space-y-1">
                    {incidentAnalysis.priority_actions.map((action, i) => (
                      <li key={i} className="text-sm text-yellow-800">• {action}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Prédictions */}
      {predictions && (
        <Card className="border-2 border-blue-300">
          <CardHeader className="bg-gradient-to-r from-blue-50 to-cyan-50">
            <CardTitle className="flex items-center">
              <Sparkles className="w-6 h-6 mr-2 text-blue-600" />
              Prédictions IA
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="space-y-4">
              {predictions.confidence_score && (
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-600">Confiance de la prédiction</p>
                  <p className="text-3xl font-bold text-blue-900">{predictions.confidence_score}%</p>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {predictions.next_7_days && (
                  <div className="p-4 border rounded-lg">
                    <h3 className="font-bold mb-2">📅 Prochains 7 jours</h3>
                    <p className="text-sm text-gray-700">{JSON.stringify(predictions.next_7_days)}</p>
                  </div>
                )}

                {predictions.next_30_days && (
                  <div className="p-4 border rounded-lg">
                    <h3 className="font-bold mb-2">📆 Prochains 30 jours</h3>
                    <p className="text-sm text-gray-700">{JSON.stringify(predictions.next_30_days)}</p>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recommandations */}
      {recommendations && (
        <Card className="border-2 border-green-300">
          <CardHeader className="bg-gradient-to-r from-green-50 to-emerald-50">
            <CardTitle className="flex items-center">
              <TrendingUp className="w-6 h-6 mr-2 text-green-600" />
              Recommandations Stratégiques IA
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="space-y-4">
              {Object.entries(recommendations).map(([key, value]) => {
                // Traduction des titres
                const titres = {
                  'optimisation_securite': '🔒 Optimisation de la Sécurité',
                  'economies_possibles': '💰 Économies Possibles',
                  'recommandations_personnel': '👥 Recommandations Personnel',
                  'besoins_formation': '📚 Besoins en Formation',
                  'suggestions_technologie': '💻 Suggestions Technologie',
                  'verification_conformite': '✅ Vérification Conformité',
                  // Anciens noms en anglais (fallback)
                  'security_optimization': '🔒 Optimisation de la Sécurité',
                  'cost_savings': '💰 Économies Possibles',
                  'staffing_recommendations': '👥 Recommandations Personnel',
                  'training_needs': '📚 Besoins en Formation',
                  'technology_suggestions': '💻 Suggestions Technologie',
                  'compliance_check': '✅ Vérification Conformité'
                };
                
                const titre = titres[key] || key.replace(/_/g, ' ');
                
                return (
                  <div key={key} className="p-4 border rounded-lg bg-white hover:shadow-md transition-shadow">
                    <h3 className="font-bold mb-3 text-gray-800">{titre}</h3>
                    {Array.isArray(value) ? (
                      <ul className="space-y-2">
                        {value.map((item, i) => (
                          <li key={i} className="flex items-start space-x-2">
                            <span className="text-green-600 mt-1">•</span>
                            <span className="text-sm text-gray-700">{item}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-sm text-gray-700">{typeof value === 'object' ? JSON.stringify(value) : value}</p>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Chat Assistant */}
      <Card className="border-2 border-pink-300">
        <CardHeader className="bg-gradient-to-r from-pink-50 to-purple-50">
          <CardTitle className="flex items-center">
            <MessageSquare className="w-6 h-6 mr-2 text-pink-600" />
            Assistant IA Conversationnel
          </CardTitle>
          <CardDescription>
            Posez vos questions à l'IA experte en sécurité privée
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="space-y-4">
            <Textarea
              placeholder="Exemple: Comment optimiser mes coûts de sécurité ? Quelles formations pour mes agents ?"
              value={chatQuestion}
              onChange={(e) => setChatQuestion(e.target.value)}
              rows={3}
            />
            <Button 
              onClick={askAssistant} 
              disabled={chatLoading}
              className="w-full bg-gradient-to-r from-pink-600 to-purple-600"
            >
              {chatLoading ? 'L\'IA réfléchit...' : 'Poser la question'}
            </Button>

            {chatResponse && (
              <div className="p-4 bg-gray-50 rounded-lg border-l-4 border-purple-600">
                <p className="text-sm font-semibold text-purple-900 mb-2">🤖 Réponse de l'IA:</p>
                <p className="text-sm text-gray-800 whitespace-pre-wrap">{chatResponse}</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AIInsights;
